<!--
    @component
    Generates an SVG stacked bar chart and sets the color via an ordinal scale in `zScale`.
   -->
<script>
    // The `containerHeight` prop that we pass to LayerCake is to make a
    // "best guess" for the height of the SVG container to use during SSR - the
    // values don't need to match exactly because they get "fixed" by JS, but
    // the closer they are, the less annoying flash the user sees + it shows up
    // better for users without JS / with slow internet connections.
    // The current values *roughly* match CSS for these various legend items as
    // they appear on mobile (search repo for `.legend-svg` - some heights are
    // set in this file, some in _app-styles.scss). There's at least one
    // dubious rem --> px conversion, but again, the numbers don't have to match
    // exactly, just provide the best estiamte possible. (This appropximation
    // could be avoided with LayerCake's ScaledSvg feature, but that would
    // require rewriting a lot of components, for the map as well as the legend)
    import { LayerCake, Svg } from "layercake";
    import { scalePow, scaleLinear } from "d3-scale";
    // @ts-expect-error (dataset: import)
    import highest_mni_in_a_county from "dataset:../_data/summary/highest_mni_in_a_county.csv";
    import Spikes from "../_components/Spikes.svelte";
    import ColorLegend from "../_components/ColorLegend.svelte";
    import EmptySquare from "./EmptySquare.svelte";
    import InstitutionLegend from "./InstitutionLegend.svelte";
    // @ts-expect-error (dataset: import)
    import guff from "dataset:../_data/guff.aml";
    import { get_tribe_noun } from "../scripts/helpers";

    /** @type import("../scripts/charts.js").PageContext */
    export let ctx;
    export let tribe_type = "tribe";

    // For safety, assume small (mobile) window as initial value. This should be
    // immediately overwritten by binding when component is hydrated/mounted.
    let window_width = 375;
    $: onMouseEvents = window_width > 700;

    let spikeHeightScale = scaleLinear().domain([800, 1500]).range([150, 400]);
    $: spikeMaxHeight = Math.max(
        Math.min(spikeHeightScale(window_width), 400),
        150
    ); // unit: pixels400;

    let spikeWidthScale = scaleLinear().domain([800, 1500]).range([3, 6]);
    $: spike_width = Math.max(Math.min(spikeWidthScale(window_width), 6), 3);

    var spikeMaxVal = highest_mni_in_a_county[0].number_mni;

    $: length = scalePow([0, spikeMaxVal], [0, spikeMaxHeight]).exponent(1);

    /**
     * @param {number} length
     */
    function spike(length) {
        return `M ${-spike_width / 2},0L0,${-length}L${spike_width / 2},0`;
    }

    var legend_rect_spacing = 20;
</script>

<svelte:window bind:innerWidth={window_width} />

<div class="legend-container center">
    <div class="legend-group legend-group__county">
        <div class="legend-row legend-row-hed">
            <div class="legend-left legend-hed">{guff.legend_county}</div>
            <div class="legend-right" />
        </div>
        <div class="legend-row">
            <div class="legend-left legend-text">
                {ctx["type"] == "homepage"
                    ? guff.legend_spike
                    : ctx["type"] == "institution"
                    ? guff.legend_institution_spike
                    : guff.legend_tribe_spike}
                {ctx["type"] == "tribe" ? get_tribe_noun(tribe_type) : ""}
            </div>
            <div class="legend-right legend-svg legend-svg-spike">
                <LayerCake ssr={true} containerHeight={37}>
                    <Svg>
                        <Spikes {ctx} {length} {spike} {legend_rect_spacing} />
                    </Svg>
                </LayerCake>
            </div>
        </div>
        {#if ctx["type"] != "tribe"}
            <div class="legend-row">
                <div class="legend-left legend-text">
                    {ctx["type"] == "homepage"
                        ? guff.legend_color
                        : guff.legend_institution_color}
                </div>
                <div class="legend-right legend-svg legend-svg-color">
                    <LayerCake ssr={true} containerHeight={28}>
                        <Svg>
                            <ColorLegend {legend_rect_spacing} {onMouseEvents} />
                        </Svg>
                    </LayerCake>
                </div>
            </div>
        {/if}

        <div class="legend-row">
            <div class="legend-left legend-text">
                {ctx["type"] == "homepage"
                    ? guff.legend_hatch
                    : ctx["type"] == "institution"
                    ? guff.legend_institution_hatch
                    : guff.legend_tribe_hatch}
                {ctx["type"] == "tribe" ? get_tribe_noun(tribe_type) : ""}
            </div>
            <div class="legend-right legend-svg legend-svg-hatch">
                <LayerCake ssr={true} containerHeight={32}>
                    <Svg>
                        <EmptySquare rect_width={window_width > 800 ? 18 : 14} {onMouseEvents} />
                    </Svg>
                </LayerCake>
            </div>
        </div>
    </div>

    <div class="legend-group legend-group__institution">
        {#if ctx["type"] != "homepage"}
            <div class="legend-row legend-row-hed">
                <div class="legend-left legend-hed">
                    {ctx["type"] == "tribe"
                        ? guff.legend_tribe_institution
                        : guff.legend_tribe_institution2}
                </div>

                {#if ctx["type"] != "tribe" || (ctx["type"] == "tribe" && !onMouseEvents)}
                    <div class="legend-right legend-svg legend-svg-institution-{ctx['type']}">
                        <LayerCake ssr={true} containerHeight={25}>
                            <Svg>
                                <InstitutionLegend {ctx} {onMouseEvents} />
                            </Svg>
                        </LayerCake>
                    </div>
                {:else}
                    <div class="legend-right" />
                {/if}
            </div>
            {#if ctx["type"] == "tribe" && onMouseEvents}
                <!-- show circles around institutions on larger screen sizes -->
                <div class="legend-row legend-row-institution-{ctx['type']}">
                    <div class="legend-left legend-text legend-hed">
                        {guff.legend_tribe_circle}
                        {get_tribe_noun(tribe_type)}
                    </div>
                    <div
                        class="legend-right legend-svg legend-svg-institution-{ctx[
                            'type'
                        ]}"
                    >
                        <LayerCake ssr={true} containerHeight={32}>
                            <Svg>
                                <InstitutionLegend
                                    {ctx}
                                    {onMouseEvents}
                                />
                            </Svg>
                        </LayerCake>
                    </div>
                </div>
            {/if}
        {/if}
    </div>
</div>

<style lang="scss">
    .legend-container {
        max-width: 320px;
        margin-left: 0;
    }

    .legend-text {
        font-size: var(--scale-3);
        line-height: var(--line-height-2);
        fill: var(--gray-40);
        font-weight: 400;
    }

    .legend-left {
        // text-align: right;
        min-width: 220px;
    }

    .legend-svg {
        height: 2rem;
    }

    .legend-svg-hatch {
        height: 2rem;
    }

    .legend-row-institution-institution {
        margin-top: var(--spacing-2);
    }

    .legend-svg-institution-institution {
        height: 25px;
    }

    .legend-svg-institution-tribe {
        height: 1.5rem;
    }

    .legend-svg:first-of-type {
        height: 45px;
    }

    .legend-row {
        display: flex;
        align-items: end;
    }

    .legend-left,
    .legend-right {
        flex: 1;
    }

    .legend-row-hed {
        padding-top: var(--spacing-1) !important;
        padding-bottom: 0 !important;
    }
</style>
